// Auth & public onboarding endpoints (login, logout, signup, email
// verification, invite validation/acceptance).

import type {
  SessionResponse,
  SignupRequest,
  SignupResponse,
  InviteInfo,
  AcceptInviteRequest,
} from '@/types/api';
import { apiFetch, API_BASE } from '@/lib/http';
import { clearTokens } from '@/lib/auth';
import { z } from 'zod';
import {
  sessionResponseSchema, signupResponseSchema, inviteInfoSchema,
} from './schema';

export async function login(email: string, password: string): Promise<SessionResponse> {
  return apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    schema: sessionResponseSchema,
  });
}

export async function logout(): Promise<void> {
  // No body: the refresh token lives in the httpOnly cookie, which the
  // backend reads directly to revoke it and clear the cookie.
  await apiFetch('/auth/logout', { method: 'POST' });
  clearTokens();
}

export async function getGoogleAuthURL(): Promise<{ url: string }> {
  return apiFetch('/auth/google', { schema: z.object({ url: z.string() }) });
}

// --- Signup & Verification (public, no auth) ---

export async function signUp(data: SignupRequest): Promise<SignupResponse> {
  const response = await fetch(`${API_BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Signup failed');
  }
  return signupResponseSchema.parse(await response.json());
}

export async function verifyEmail(token: string): Promise<SessionResponse> {
  const response = await fetch(`${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.code || 'Verification failed');
  }
  return sessionResponseSchema.parse(await response.json());
}

export async function resendVerification(email: string): Promise<void> {
  await fetch(`${API_BASE}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

// --- Invite (public) ---

export async function validateInvite(token: string): Promise<InviteInfo> {
  const response = await fetch(`${API_BASE}/invites/${encodeURIComponent(token)}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.code || 'Invalid invite');
  }
  return inviteInfoSchema.parse(await response.json());
}

export async function acceptInvite(data: AcceptInviteRequest): Promise<SessionResponse> {
  const response = await fetch(`${API_BASE}/auth/signup/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept invite');
  }
  return sessionResponseSchema.parse(await response.json());
}
